import APITemplate from '@api/APITemplate'
import { diffTimeMinutes } from '@utils/Format'
import { downloadBase64 } from '@utils/Download'
import {
  validateRequiredFields,
  buildUrl,
  getDriveScope,
  getDriveActionUrl,
  normalizeUniqueIds,
  normalizeDriveUpdatePayloadItems,
  buildDeleteResponseValueFromBatchItem,
} from '@modules/aawz-drive/utils/aawz-drive'
import store from '@state/store'
import con from '@services/connection'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import { aawzDrive } from '@modules/aawz-drive/constants/URL.js'

// import { getMock } from '@modules/aawz-drive/mocks/aawz-drive'

class APIAawzDrive extends APITemplate {
  constructor() {
    super()
    this.initRequests()
    this.initStoreBindings()
  }

  initRequests() {
    this.MODULE = 'aawz-drive'
    this.reqCarregar = null
    this.uploadPending = 0
  }

  initStoreBindings() {
    // STORE KEYS
    this.KEY_DATA = `${this.MODULE}/aawzDrive`
    this.KEY_LOADING = `${this.MODULE}/aawzDriveLoading`
    this.KEY_LAST_UPDATE = `${this.MODULE}/aawzDriveLastUpdate`
    this.KEY_ALLOWED_EXTENSIONS = `${this.MODULE}/aawzDriveAllowedExtensions`

    // STORE ACTIONS
    this.ACTION_SET_DATA = `${this.MODULE}/setAawzDrive`
    this.ACTION_SET_LOADING = `${this.MODULE}/setAawzDriveLoading`
    this.ACTION_SET_UPLOAD_LOADING = `${this.MODULE}/setAawzDriveUploadLoading`
    this.ACTION_ADD = `${this.MODULE}/addAawzDrive`
    this.ACTION_DELETE = `${this.MODULE}/deleteAawzDrive`
    this.ACTION_RENAME = `${this.MODULE}/renameAawzDriveItem`
    this.ACTION_SET_STORAGE = `${this.MODULE}/setAawzDriveStorage`
    this.ACTION_SET_ALLOWED_EXTENSIONS = `${this.MODULE}/setAawzDriveAllowedExtensions`
  }

  // ---------- HELPERS ----------
  shouldUseCache(forceUpdate, rootKey) {
    if (forceUpdate) return false

    const cachedValue = this.getDataByRootKey(rootKey)
    if (!cachedValue) {
      return false
    }

    return !diffTimeMinutes(
      store.getters[this.KEY_LAST_UPDATE],
      MINUTES_TIMEOUT_REQUESTS
    )
  }

  getDataByRootKey(rootKey) {
    const getter = store.getters[this.KEY_DATA]
    if (typeof getter === 'function') {
      return getter(rootKey)
    }
    return null
  }

  setDataFromResponse(r, rootKey) {
    if (r && r.status === 200) {
      store.dispatch(this.ACTION_SET_DATA, {
        rootKey,
        data: r.value || null,
      })
      return
    }

    store.dispatch(this.ACTION_SET_DATA, {
      rootKey,
      data: null,
    })
  }

  addItemFromResponse(r, rootKey) {
    if (!r || r.status !== 200) {
      return
    }
    store.dispatch(this.ACTION_ADD, {
      rootKey,
      data: r.value || null,
    })
    this.updateStorageFromResponse(r, rootKey)
  }

  deleteItemFromResponse(r, rootKey) {
    if (!r || r.status !== 200) {
      return
    }

    const payload = r.value || null
    store.dispatch(this.ACTION_DELETE, {
      rootKey,
      data: payload,
    })
    this.updateStorageFromResponse(r, rootKey)
  }

  updateStorageFromResponse(r, rootKey) {
    const storage = r && r.value ? r.value.storage : null
    if (!storage) {
      return
    }

    store.dispatch(this.ACTION_SET_STORAGE, {
      rootKey,
      storage,
    })
  }

  renameItemFromResponse(r, rootKey, targetId) {
    if (!r || r.status !== 200) {
      return
    }

    let item = r.value && r.value.item ? r.value.item : null

    // Folder rename returns value.items (plural) instead of value.item
    if (!item && r.value && Array.isArray(r.value.items) && targetId) {
      const flatItems = r.value.items.reduce(function (acc, entry) {
        if (Array.isArray(entry)) {
          return acc.concat(entry)
        }
        if (entry && typeof entry === 'object') {
          acc.push(entry)
        }
        return acc
      }, [])
      item = flatItems.find(function (i) {
        return i && String(i.id) === String(targetId)
      }) || null
    }

    if (!item && !targetId) {
      return
    }

    store.dispatch(this.ACTION_RENAME, {
      rootKey,
      data: item || { id: targetId },
    })
    this.updateStorageFromResponse(r, rootKey)
  }

  async getEarlyResponse(forceUpdate, rootKey) {
    if (this.reqCarregar) {
      const r = await this.reqCarregar
      return (
        r ||
        this.buildErrorResponse({
          message: 'Erro ao carregar dados',
        })
      )
    }

    const cachedValue = this.getDataByRootKey(rootKey)

    if (this.shouldUseCache(forceUpdate, rootKey)) {
      return this.buildSuccessResponse({
        message: 'Dados carregados da memória',
        value: cachedValue,
      })
    }

    if (store.getters[this.KEY_LOADING]) {
      if (!cachedValue) {
        return null
      }
      return this.buildSuccessResponse({
        message: 'Requisição em andamento, dados carregados da memória',
        value: cachedValue,
      })
    }

    return null
  }

  getScope(scope) {
    return getDriveScope(scope)
  }

  getActionUrl({ scope, rootKey, itemId, action, folder }) {
    return getDriveActionUrl({ scope, rootKey, itemId, action, folder })
  }

  getUploadValidation({ file, folder, rootKey, scope }) {
    const currentScope = this.getScope(scope)
    const required =
      currentScope === 'office' ? [file, folder] : [file, folder, rootKey]

    return validateRequiredFields({
      required,
      message: 'Arquivo inválido.',
    })
  }

  getOfficeClientPayload(folder = {}) {
    if (folder.id_cliente_app) {
      return { id_cliente_app: folder.id_cliente_app }
    }

    if (folder.id_cliente) {
      return { id_cliente: folder.id_cliente }
    }

    return {}
  }

  buildUploadPayload(file, folder, scope = 'client') {
    const currentScope = this.getScope(scope)
    const officeClientPayload =
      currentScope === 'office' ? this.getOfficeClientPayload(folder) : {}

    return {
      file,
      name: file.name || 'Arquivo',
      folder_path: folder.id,
      ...officeClientPayload,
    }
  }

  deleteItemsFromBatchResponse(r, rootKey) {
    if (!r || r.status !== 200) {
      return
    }

    const responseItems = r.value && Array.isArray(r.value.items) ? r.value.items : []
    responseItems.forEach((responseItem) => {
      if (!responseItem || responseItem.status !== 200) {
        return
      }

      const value = buildDeleteResponseValueFromBatchItem(
        responseItem.value || {}
      )
      if (!value) {
        return
      }

      this.deleteItemFromResponse(
        {
          status: 200,
          value,
        },
        rootKey
      )
    })
  }

  getDriveUpdateItemsById(items = []) {
    const itemsById = {}

    items.forEach((item) => {
      const itemId =
        item && item.id !== null && item.id !== undefined ? String(item.id) : ''
      if (!itemId || itemsById[itemId]) {
        return
      }
      itemsById[itemId] = item
    })

    return itemsById
  }

  resolveUpdatedIdsFromResponseItem(responseItem, fallbackItem = null) {
    const responseValue = responseItem && responseItem.value
      ? responseItem.value
      : {}
    const parsedValue = buildDeleteResponseValueFromBatchItem(responseValue)
    const parsedIds =
      parsedValue && Array.isArray(parsedValue.items)
        ? parsedValue.items
            .map((item) => item && item.id)
            .filter(Boolean)
            .map((id) => String(id))
        : []

    if (parsedIds.length) {
      return parsedIds
    }

    const fallbackId =
      fallbackItem && fallbackItem.id !== null && fallbackItem.id !== undefined
        ? String(fallbackItem.id)
        : ''

    return fallbackId ? [fallbackId] : []
  }

  resolveUpdatedParent(responseItem, requestItem = null) {
    const folderPath =
      requestItem && requestItem.folder_path ? String(requestItem.folder_path) : ''
    if (folderPath) {
      return {
        id: folderPath,
        type: 'folder',
      }
    }

    const responseValue = responseItem && responseItem.value
      ? responseItem.value
      : {}
    const responseParent = responseValue.parent
    if (responseParent && responseParent.id) {
      return responseParent
    }
    return null
  }

  syncUpdatedItemToStore({ rootKey, item, parent, storage }) {
    const itemId =
      item && item.id !== null && item.id !== undefined ? String(item.id) : ''
    if (!itemId || !parent || !parent.id) {
      return
    }

    this.deleteItemFromResponse(
      {
        status: 200,
        value: {
          items: [{ id: itemId }],
        },
      },
      rootKey
    )

    this.addItemFromResponse(
      {
        status: 200,
        value: {
          parent,
          item: {
            ...item,
            id: itemId,
          },
          storage: storage || null,
        },
      },
      rootKey
    )
  }

  syncUpdatedItemsFromBatchResponse(r, rootKey, requestItems = []) {
    if (!r || r.status !== 200) {
      return
    }

    const safeRequestItems = Array.isArray(requestItems) ? requestItems : []
    if (!safeRequestItems.length) {
      return
    }

    const requestItemsById = this.getDriveUpdateItemsById(safeRequestItems)
    const responseItems = r.value && Array.isArray(r.value.items) ? r.value.items : []

    if (!responseItems.length) {
      safeRequestItems.forEach((requestItem) => {
        this.syncUpdatedItemToStore({
          rootKey,
          item: requestItem,
          parent: this.resolveUpdatedParent(null, requestItem),
          storage: r.value && r.value.storage ? r.value.storage : null,
        })
      })
      return
    }

    responseItems.forEach((responseItem, index) => {
      if (!responseItem || responseItem.status !== 200) {
        return
      }

      const fallbackItem = safeRequestItems[index] || null
      const updatedIds = this.resolveUpdatedIdsFromResponseItem(
        responseItem,
        fallbackItem
      )
      const parent = this.resolveUpdatedParent(responseItem, fallbackItem)
      const storage =
        responseItem.value && responseItem.value.storage
          ? responseItem.value.storage
          : r.value && r.value.storage
          ? r.value.storage
          : null

      updatedIds.forEach((itemId) => {
        const requestItem = requestItemsById[itemId] || fallbackItem
        if (!requestItem) {
          return
        }

        this.syncUpdatedItemToStore({
          rootKey,
          item: requestItem,
          parent,
          storage,
        })
      })
    })
  }

  postUpload({ file, folder, rootKey, scope }) {
    const currentScope = this.getScope(scope)
    const url = this.getActionUrl({
      scope: currentScope,
      rootKey,
      action: 'upload',
      folder,
    })

    return con.post(url, this.buildUploadPayload(file, folder, currentScope))
  }

  // ---------- REQUESTS ----------
  async carregar({
    forceUpdate = false,
    rootKey = null,
    drive_root_name = 'AAWZ Drive',
    scope = 'client',
  } = {}) {
    const earlyResponse = await this.getEarlyResponse(forceUpdate, rootKey)
    if (earlyResponse) return earlyResponse

    store.dispatch(this.ACTION_SET_LOADING, true)

    this.getAllowedExtensions({ forceUpdate: false })

    this.reqCarregar = con.get(buildUrl({ scope, rootKey, drive_root_name }))
    // this.reqCarregar = Promise.resolve(getMock)
    const r = await this.reqCarregar

    this.setDataFromResponse(r, rootKey)

    store.dispatch(this.ACTION_SET_LOADING, false)
    this.reqCarregar = null

    return (
      r ||
      this.buildErrorResponse({
        message: 'Erro ao carregar dados',
      })
    )
  }

  async uploadItem({
    file = {},
    folder = {},
    rootKey = null,
    scope = 'client',
  } = {}) {
    const validationError = this.getUploadValidation({
      file,
      folder,
      rootKey,
      scope,
    })

    if (validationError) {
      return validationError
    }

    this.uploadPending += 1
    if (this.uploadPending === 1) {
      store.dispatch(this.ACTION_SET_UPLOAD_LOADING, true)
    }

    try {
      const r = await this.postUpload({ file, folder, rootKey, scope })

      this.addItemFromResponse(r, rootKey)

      return r || {}
    } finally {
      this.uploadPending = Math.max(0, this.uploadPending - 1)
      if (this.uploadPending === 0) {
        store.dispatch(this.ACTION_SET_UPLOAD_LOADING, false)
      }
    }
  }

  async downloadItem({
    item = {},
    rootKey = null,
    download = true,
    scope = 'client',
  } = {}) {
    const currentScope = this.getScope(scope)
    if (!item.id || (currentScope !== 'office' && !rootKey)) {
      return {
        status: 400,
        message: 'Arquivo inválido.',
      }
    }

    const url = this.getActionUrl({
      scope: currentScope,
      rootKey,
      itemId: item.id,
      action: 'download',
    })

    const r = await con.get(url)

    if (r.status === 200 && download) {
      const { base64, name, extension } = r.value[0] || r.value || {}
      downloadBase64(base64, name, extension)
    }

    return r || {}
  }

  async downloadItems({
    itemIds = [],
    rootKey = null,
    download = true,
    scope = 'client',
  } = {}) {
    const currentScope = this.getScope(scope)
    const safeItemIds = Array.isArray(itemIds)
      ? itemIds.filter(Boolean).map(String)
      : []

    if (!safeItemIds.length || (currentScope !== 'office' && !rootKey)) {
      return {
        status: 400,
        message: 'Lista de itens inválida.',
      }
    }

    const url = this.getActionUrl({
      scope: currentScope,
      rootKey,
      action: 'downloadItems',
    })

    const r = await con.post(url, {
      ids: safeItemIds,
    })

    if (r.status === 200 && download) {
      const { base64, name, extension } = r.value[0] || r.value || {}
      downloadBase64(base64, name, extension)
    }

    return r || {}
  }

  async deleteItems({ itemIds = [], rootKey = null, scope = 'client' } = {}) {
    const currentScope = this.getScope(scope)
    const safeItemIds = normalizeUniqueIds(itemIds)

    if (!safeItemIds.length || (currentScope !== 'office' && !rootKey)) {
      return {
        status: 400,
        message: 'Lista de itens inválida.',
      }
    }

    const url = this.getActionUrl({
      scope: currentScope,
      rootKey,
      action: 'deleteItems',
    })

    const r = await con.post(url, {
      ids: safeItemIds,
    })

    this.deleteItemsFromBatchResponse(r, rootKey)

    return r || {}
  }

  async updateItems({ items = [], rootKey = null, scope = 'client' } = {}) {
    const currentScope = this.getScope(scope)
    const safeItems = normalizeDriveUpdatePayloadItems(items)

    if (!safeItems.length || (currentScope !== 'office' && !rootKey)) {
      return {
        status: 400,
        message: 'Lista de itens inválida.',
      }
    }

    const url = this.getActionUrl({
      scope: currentScope,
      rootKey,
      action: 'updateItems',
    })

    const r = await con.put(url, {
      items: safeItems,
    })

    this.syncUpdatedItemsFromBatchResponse(r, rootKey, safeItems)

    return r || {}
  }

  updateItem({ item = {}, rootKey = null, scope = 'client' } = {}) {
    const safeItems = normalizeDriveUpdatePayloadItems([item])
    if (!safeItems.length) {
      return {
        status: 400,
        message: 'Item inválido.',
      }
    }

    return this.updateItems({
      items: safeItems,
      rootKey,
      scope,
    })
  }

  async deleteItem({ item = {}, rootKey = null, scope = 'client' } = {}) {
    const currentScope = this.getScope(scope)
    const validationError = validateRequiredFields({
      required:
        currentScope === 'office'
          ? [item && item.id]
          : [item && item.id, rootKey],
      message: 'Item inválido.',
    })

    if (validationError) {
      return validationError
    }

    const url = this.getActionUrl({
      scope: currentScope,
      rootKey,
      itemId: item.id,
      action: 'delete',
    })

    const r = await con.delet(url)

    this.deleteItemFromResponse(r, rootKey)

    return r || {}
  }

  async criarPasta({ folder, name, rootKey = null, scope = 'client' } = {}) {
    const currentScope = this.getScope(scope)
    const validationError = validateRequiredFields({
      required:
        currentScope === 'office' ? [folder, name] : [folder, name, rootKey],
      message: 'Pasta inválida.',
    })

    if (validationError) {
      return validationError
    }

    const folder_path = `${folder.id}/${name}`

    const data = {
      folder_path,
      ...(currentScope === 'office' ? this.getOfficeClientPayload(folder) : {}),
    }

    const url = this.getActionUrl({
      scope: currentScope,
      rootKey,
      action: 'createFolder',
      folder,
    })

    const r = await con.post(url, data)

    this.addItemFromResponse(r, rootKey)

    return r || {}
  }

  async renameFolderFile({ item, rootKey = null, scope = 'client' } = {}) {
    const currentScope = this.getScope(scope)
    const validationError = validateRequiredFields({
      required:
        currentScope === 'office'
          ? [item && item.id, item && item.name]
          : [item && item.id, item && item.name, rootKey],
      message: 'Item inválido.',
    })

    if (validationError) {
      return validationError
    }

    const data = {
      name: item.name,
    }

    const url = this.getActionUrl({
      scope: currentScope,
      rootKey,
      itemId: item.id,
      action: 'rename',
    })

    const r = await con.put(url, data)

    this.renameItemFromResponse(r, rootKey, item.id)

    return r || {}
  }

  async getAllowedExtensions({ forceUpdate = false } = {}) {
    const allowedExtensions = store.getters[this.KEY_ALLOWED_EXTENSIONS]
    if (!forceUpdate && allowedExtensions && allowedExtensions.length) {
      return this.buildSuccessResponse({
        message: 'Dados carregados da memória',
        value: {
          extensions: allowedExtensions,
        },
      })
    }

    let response = null

    try {
      response = await con.get(aawzDrive.getAllowedExtensions)
    } catch (error) {
      console.error(
        'Erro ao carregar extensões permitidas do AAWZ Drive:',
        error
      )
    } finally {
      store.dispatch(this.ACTION_SET_ALLOWED_EXTENSIONS, response || {})
    }

    return (
      response ||
      this.buildErrorResponse({
        message: 'Erro ao carregar extensões permitidas',
      })
    )
  }
}

const apiAawzDrive = new APIAawzDrive()
export { apiAawzDrive }
